import type { ReactNode } from 'react';
import { lazy, Suspense } from 'react';
import { Link, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { BrandResponse, StoreResponse } from '@clarity/contract';
import { useSession } from '../lib/session.js';
import { ClientContextProvider, useClientContext } from '../lib/clientContext.js';
import { ScopeProvider, resolveScope, useScopeContext } from '../lib/scopeContext.js';
import { Shell } from './Shell.js';
import { Clients } from '../features/Clients.js';
import { Brands } from '../features/Brands.js';
import { Settings } from '../features/Settings.js';
import { IngestionRuns } from '../features/admin/IngestionRuns.js';
import { MonthlyOfferReview } from '../features/admin/MonthlyOfferReview.js';
import { BannerOptionsAdmin } from '../features/admin/BannerOptionsAdmin.js';
import { LocationsAdmin } from '../features/admin/LocationsAdmin.js';
import { CampaignsOverview } from '../features/admin/CampaignsOverview.js';
import { Connections } from '../features/admin/Connections.js';
import { PayoutScheduleAdmin } from '../features/admin/PayoutScheduleAdmin.js';
import { Subscription } from '../features/Subscription.js';
import { AdvisorUsageAdmin } from '../features/admin/AdvisorUsageAdmin.js';
import { WorkspaceUsers } from '../features/WorkspaceUsers.js';
import { WorkspaceSurface } from '../features/WorkspaceSurface.js';
import { StorePayoutDetail } from '../features/store/StorePayoutDetail.js';
import { CampaignDetail } from '../features/campaigns/CampaignDetail.js';
import { TermDetail } from '../features/campaigns/TermDetail.js';
import { OccurrenceDetail } from '../features/campaigns/OccurrenceDetail.js';
import { MonthlyReviewPage } from '../features/campaigns/MonthlyReviewPage.js';
import { PickBranch } from '../features/PickBranch.js';
import { SURFACES, SURFACE_CAPABILITY, type SurfaceId } from '../features/nav.js';
import { NotAuthorized } from '../features/Placeholder.js';
import { useBrands, useStore, useStores } from '../queries/index.js';
import { isNotYetOnboarded } from '../lib/lifecycle.js';

/**
 * The authenticated route tree, mirroring the two-level IA as real URLs:
 *
 *   /clients                              admin — client list (home)
 *   /brands                               admin — discover/import hub + overview
 *   /settings                             admin — global per-platform fee defaults
 *   /ingestion                            admin — ingestion run history + Run now
 *   /monthly-offers                       admin — monthly-offer OCR review + save
 *   /banner-options                       admin — platform banner options (publish to owners)
 *   /connections                          admin — live platform-connection status
 *   /clients/:clientId/subscription       admin — one client's subscription + invoices
 *   /c/:clientId                          client workspace — DEFAULT SCOPE redirect
 *   /c/:clientId/all/<surface>            client workspace — All-stores scope
 *   /c/:clientId/brand/:brandId/<surface> client workspace — Brand scope
 *   /c/:clientId/store/:storeId/<surface> client workspace — Branch scope
 *   /c/:clientId/brands                   client workspace — manage brands/branches
 *
 * `Shell` is the layout (rail + topbar + <Outlet>). The client context derives
 * from the `:clientId` segment, so back/forward and deep links just work. The
 * SCOPE (all | brand | store) lives in the path segment after the client
 * (clarity-navigation-scope); the legacy `?store=` picker + the bare
 * `onboarding`/`data`/`dashboard` routes are gone (task 7.1).
 *
 * Role gating: a client-role user is redirected from the admin routes into its
 * own workspace; an admin landing at `/` goes to `/clients`. The server enforces
 * tenant isolation independently.
 */
/*
 * INTERNAL dev-only backtest (clarity-payout-backtest). Lazy-imported behind
 * `import.meta.env.DEV` so a PRODUCTION build tree-shakes the whole module out
 * of the bundle — the surface does not merely become unreachable, it is not
 * shipped at all. `null` in a prod build, so the route below is never added.
 */
const PayoutBacktest = import.meta.env.DEV
  ? lazy(() => import('../features/admin/PayoutBacktest.js').then((m) => ({ default: m.PayoutBacktest })))
  : null;

export function AppRoutes() {
  const { role, clientId: ownClientId } = useSession();
  const isAdmin = role === 'admin';

  // Where "home" (`/`) and any unknown route send each role. A client user lands
  // on the bare `/c/:clientId`, which `DefaultScopeRedirect` resolves to the
  // client's default scope (Req 7: one branch → that branch, incl. onboarding
  // for a not-yet-onboarded default; many → All; none → empty state).
  const home = isAdmin ? '/clients' : `/c/${ownClientId}`;

  return (
    <ClientContextProvider>
      <Routes>
        <Route element={<Shell />}>
          {/* Admin top level */}
          <Route
            path="/clients"
            element={isAdmin ? <Clients /> : <Navigate to={home} replace />}
          />
          <Route
            path="/brands"
            element={isAdmin ? <Brands /> : <Navigate to={home} replace />}
          />
          <Route
            path="/settings"
            element={isAdmin ? <Settings /> : <Navigate to={home} replace />}
          />
          <Route
            path="/ingestion"
            element={isAdmin ? <IngestionRuns /> : <Navigate to={home} replace />}
          />
          <Route
            path="/monthly-offers"
            element={isAdmin ? <MonthlyOfferReview /> : <Navigate to={home} replace />}
          />
          <Route
            path="/banner-options"
            element={isAdmin ? <BannerOptionsAdmin /> : <Navigate to={home} replace />}
          />
          <Route
            path="/locations"
            element={isAdmin ? <LocationsAdmin /> : <Navigate to={home} replace />}
          />
          <Route
            path="/campaigns-overview"
            element={isAdmin ? <CampaignsOverview /> : <Navigate to={home} replace />}
          />
          <Route
            path="/payout-schedule"
            element={isAdmin ? <PayoutScheduleAdmin /> : <Navigate to={home} replace />}
          />
          <Route
            path="/connections"
            element={isAdmin ? <Connections /> : <Navigate to={home} replace />}
          />
          {/*
           * INTERNAL dev-only backtest (clarity-payout-backtest): validates our
           * payout estimate against settled payouts. Gated by BOTH the admin role
           * AND a dev build — `PayoutBacktest` is `null` in a production bundle
           * (lazy module tree-shaken out), so the route is never added there.
           */}
          {PayoutBacktest && (
            <Route
              path="/payout-backtest"
              element={
                isAdmin ? (
                  <Suspense fallback={null}>
                    <PayoutBacktest />
                  </Suspense>
                ) : (
                  <Navigate to={home} replace />
                )
              }
            />
          )}
          <Route
            path="/clients/:clientId/subscription"
            element={isAdmin ? <Subscription /> : <Navigate to={home} replace />}
          />
          <Route
            path="/clients/:clientId/advisor-usage"
            element={isAdmin ? <AdvisorUsageAdmin /> : <Navigate to={home} replace />}
          />
          {/* Admin entry to a workspace's user logins (self-serve twin lives
              under /c/:clientId/users below). */}
          <Route
            path="/clients/:clientId/users"
            element={isAdmin ? <WorkspaceUsers /> : <Navigate to={home} replace />}
          />

          {/* Client workspace (admin-entered or client-native) */}
          <Route path="/c/:clientId" element={<ClientGuard isAdmin={isAdmin} ownClientId={ownClientId} />}>
            {/*
             * Index → DEFAULT SCOPE (clarity-navigation-scope task 3.2, Req 1.5,
             * 7.1–7.3): a bare `/c/:clientId` has no scope segment, so compute
             * the client's default scope from its loaded branches and redirect:
             * exactly one branch → that branch; more than one → All stores; none
             * → an empty state pointing to Brands.
             */}
            <Route index element={<DefaultScopeRedirect />} />

            {/*
             * ── New scope branches (clarity-navigation-scope task 3.1) ──────
             * Each branch wraps its children in <ScopeProvider> so every surface
             * reads the URL-derived scope. The six surfaces from the SURFACES
             * registry are the child routes; for now they render placeholders
             * (guards wire in tasks 3.2–3.4, real views tasks 5+6).
             */}
            <Route path="all" element={<ScopedLayout />}>
              {surfaceRoutes({ branchOnlyGuarded: true })}
            </Route>
            <Route path="brand/:brandId" element={<ScopedLayout />}>
              {surfaceRoutes({ branchOnlyGuarded: true })}
            </Route>
            <Route path="store/:storeId" element={<StoreScopedLayout />}>
              {surfaceRoutes({ branchOnlyGuarded: false })}
              {/*
               * The payout DETAIL page (clarity-orders-detail / payouts-monthly-view):
               * a payout's OWN page, a sibling of the `payouts` surface under the
               * store scope. It mounts under the same OnboardingLockGuard, so a
               * not-yet-onboarded branch is forced onto onboarding first and the
               * "Details" affordance only ever lands here for an onboarded store.
               */}
              <Route path="payouts/:payoutId" element={<StorePayoutDetail />} />
              {/*
               * The owner monthly campaign review page (clarity-monthly-campaign-
               * review Req 7): the read-only surface the WhatsApp template's URL
               * button opens — per-campaign verdict, expectation, history,
               * similar, decision + deadline, and the AM brief. Keyed by the
               * published month.
               */}
              <Route path="monthly-review/:month" element={<MonthlyReviewPage />} />
              {/*
               * The campaign DETAIL page (clarity-campaign-matching task 13.2):
               * a campaign's OWN page, a sibling of the `campaigns` surface under
               * the store scope — the row-open from the list (task 13.1) lands
               * here. Mounts under the same OnboardingLockGuard, so a
               * not-yet-onboarded branch is forced onto onboarding first.
               */}
              {/*
               * The TERM DETAIL page (clarity-campaign-terms-economics task
               * 9.3): one terms bucket's drill-down — the signature + summary
               * cards + observed-% histogram + the paginated orders table — a
               * sibling of the `campaigns` surface, reached from the Terms
               * list's "Open term details →" affordance. Registered BEFORE the
               * `campaigns/:cid` param route so the static `terms` segment never
               * resolves as a `:cid` campaign id. `:bucketKey` is the bucket's
               * signature key, URL-encoded by the list link.
               */}
              <Route path="campaigns/terms/:bucketKey" element={<TermDetail />} />
              <Route path="campaigns/:cid" element={<CampaignDetail />} />
              {/*
               * The occurrence DETAIL page (clarity-campaign-matching task 13.4):
               * one occurrence's own page — the override table + its stats + the
               * matched-orders evidence — a sibling of the campaign detail,
               * reached from a PARTICIPATED occurrence row's open. Same
               * OnboardingLockGuard, so a not-yet-onboarded branch is forced
               * onto onboarding first.
               */}
              <Route
                path="campaigns/:cid/occurrences/:oid"
                element={<OccurrenceDetail />}
              />
            </Route>

            {/*
             * Brands stays a CLIENT-LEVEL destination (manage brands/branches),
             * not a scope surface — it is the escape hatch the rail links at
             * every scope (Req 5.2). The legacy `dashboard`/`onboarding`/`data`
             * routes + the `StoreWorkspace` + `?store=` picker are gone (task
             * 7.1): the scope tree above serves every surface, and the bare
             * `/c/:clientId` index redirects to the default scope.
             */}
            <Route path="brands" element={<Brands />} />

            {/*
             * Workspace users (clarity-workspace-users): a CLIENT-LEVEL,
             * non-scoped page like Brands — the self-serve twin of the admin
             * `/clients/:clientId/users`. Reachable by the workspace's own client
             * owner (manage their teammates) and by an admin inside the client's
             * workspace. Server authorizes either way; the client can only ever
             * reach its OWN `/c/:clientId` (ClientGuard + server isolation).
             */}
            <Route path="users" element={<WorkspaceUsers />} />

            {/*
             * Any unknown path under `/c/:clientId` (a bookmarked or stale link
             * to a removed legacy route like `/dashboard`, `/onboarding`,
             * `/data`) redirects to the bare client path, which
             * `DefaultScopeRedirect` resolves to the client's default scope — so
             * old links self-heal instead of dead-ending. The genuine role gate
             * stays in `ClientGuard` (`NotAuthorized`).
             */}
            <Route path="*" element={<ClientCatchAllRedirect />} />
          </Route>

          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Route>
      </Routes>
    </ClientContextProvider>
  );
}

/**
 * Guards the client workspace: a client-role user may only enter its OWN client's
 * workspace (defense-in-depth on top of server isolation). An admin may enter any
 * client. Renders the nested route via <Outlet> through Shell's layout.
 */
function ClientGuard({ isAdmin, ownClientId }: { isAdmin: boolean; ownClientId: string | null }) {
  const { clientId } = useParams();
  if (!isAdmin && clientId !== ownClientId) {
    return <NotAuthorized />;
  }
  return <Outlet />;
}

/**
 * Catch-all for an unknown path UNDER `/c/:clientId` — a bookmarked or stale link
 * to a surface that no longer exists at that URL shape (e.g. the removed legacy
 * `/c/:clientId/dashboard`, `/onboarding`, `/data` from before task 7.1). Rather
 * than dead-end on `NotAuthorized`, it redirects to the bare `/c/:clientId`,
 * which `DefaultScopeRedirect` resolves to the client's default scope — so old
 * links self-heal to the right scoped surface. A genuine role/tenant denial is
 * still `NotAuthorized`, raised by `ClientGuard` before this ever renders.
 */
function ClientCatchAllRedirect() {
  const { clientId } = useParams();
  return <Navigate to={`/c/${clientId}`} replace />;
}

/**
 * The scope-branch layout: wraps the surface <Outlet> in <ScopeProvider>, which
 * derives the scope (all | brand | store) from the URL segments. One component
 * serves all three branches — the provider reads `:scopeKind` + the id segment
 * itself (via `useMatch`), so the branch only has to establish the context.
 *
 * `ScopeGuard` sits just inside the provider (task 3.2): it reads the resolved
 * scope and redirects an UNKNOWN `:brandId`/`:storeId` (one not in the active
 * client's set — the context's `notFound` flag) to the client's default scope
 * (Req 3.4 / 5.3 / 8.2). The branch-only + onboarding-lock guards (tasks
 * 3.3/3.4) slot in below it later.
 */
function ScopedLayout() {
  return (
    <ScopeProvider>
      <ScopeGuard />
    </ScopeProvider>
  );
}

/**
 * The STORE-scope layout (clarity-navigation-scope task 3.4). Like
 * {@link ScopedLayout} it establishes the `<ScopeProvider>` and runs the
 * `ScopeGuard` (unknown `:storeId` → default scope, Req 5.3/8.2), but it then
 * threads the surface `<Outlet>` through the {@link OnboardingLockGuard}: a store
 * scope is always a single branch, so a not-yet-onboarded branch must be forced
 * onto its onboarding surface (Req 5.3) and the rail collapsed (Req 5.2).
 *
 * `ScopeGuard` renders its children (here, the lock guard) in place of its bare
 * `<Outlet>` when the store is known; when the store is unknown it redirects to
 * the default scope before the lock guard ever mounts.
 */
function StoreScopedLayout() {
  return (
    <ScopeProvider>
      <ScopeGuard>
        <OnboardingLockGuard />
      </ScopeGuard>
    </ScopeProvider>
  );
}

/**
 * ScopeGuard (task 3.2, Req 3.4 / 5.3 / 8.2): runs INSIDE a `<ScopeProvider>` so
 * it can read `useScopeContext().notFound` — true when the URL named a brand or
 * store that is not in the active client's narrowed set (out-of-tenant, deleted,
 * or simply wrong). It mirrors the server's `404`: rather than render cross-tenant
 * chrome, redirect to the client's default scope, which `DefaultScopeRedirect`
 * then resolves (one branch → that branch, else All stores, none → empty state).
 *
 * While the brand/store lists are still loading, the context resolves to `all`
 * with `notFound: false`, so a transient flag never fires a wrong redirect; the
 * guard only redirects once the data is loaded and the id is genuinely absent.
 */
function ScopeGuard({ children }: { children?: ReactNode }) {
  const { clientId } = useParams();
  const { notFound } = useScopeContext();
  const brands = useBrands();
  const stores = useStores();

  // Only trust `notFound` once both lists have loaded — a `notFound` computed
  // against an empty-because-still-loading set would redirect a valid deep link.
  const loaded =
    brands.data?.status === 200 && stores.data?.status === 200;

  if (loaded && notFound && clientId) {
    return <Navigate to={`/c/${clientId}`} replace />;
  }
  // When a store-scope layout nests its own guard (the OnboardingLockGuard), run
  // it in place of the bare surface `<Outlet>`; the all/brand layouts pass no
  // children and fall through to the surface routes directly.
  return <>{children ?? <Outlet />}</>;
}

/**
 * DefaultScopeRedirect (task 3.2, Req 1.5 / 7.1–7.3): the element for the bare
 * `/c/:clientId` index. It has no scope segment, so it computes the client's
 * default scope from the loaded brand/store lists — reusing the same pure
 * `resolveScope` narrowing the scope context uses — and `<Navigate>`s to it:
 *
 *   - exactly ONE branch  → that branch (`/store/:storeId/dashboard`); the
 *     OnboardingLockGuard (task 3.4) then forces a not-yet-onboarded branch onto
 *     its onboarding surface (Req 7.4), so landing on `dashboard` is correct here;
 *   - MORE than one branch → All stores (`/all/dashboard`) (Req 7.2);
 *   - NO branches          → an empty state pointing to Brands (Req 7.3 / 2.6).
 *
 * While the lists load it renders a loading state rather than redirecting
 * prematurely (a premature redirect would land on the empty state and bounce).
 */
function DefaultScopeRedirect() {
  const { clientId } = useParams();
  const { role } = useSession();
  const { activeClientId } = useClientContext();
  const brands = useBrands();
  const stores = useStores();

  const loaded = brands.data?.status === 200 && stores.data?.status === 200;
  const isError = brands.isError || stores.isError;

  if (!loaded) {
    if (isError) {
      return (
        <section>
          <h2>Dashboard</h2>
          <p className="muted">Could not load this client's branches.</p>
        </section>
      );
    }
    return (
      <section aria-busy="true">
        <h2>Dashboard</h2>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  const brandRows: BrandResponse[] = brands.data?.status === 200 ? brands.data.body : [];
  const storeRows: StoreResponse[] = stores.data?.status === 200 ? stores.data.body : [];

  // Resolve the in-client branch set via the shared narrowing (the `all` scope
  // resolution returns every in-client branch, which is exactly what the default
  // decision needs).
  const resolved = resolveScope(
    { scopeKind: 'all' },
    brandRows,
    storeRows,
    { role, selectedClientId: activeClientId },
  );
  const branches = resolved.branches;

  if (!clientId) {
    return <Navigate to="/" replace />;
  }

  // Req 7.3 / 2.6 — no branches: an empty state pointing to Brands, not a scoped
  // surface (there is nothing to scope to).
  if (branches.length === 0) {
    return <NoBranchesEmptyState clientId={clientId} />;
  }

  // Req 7.1 — exactly one branch → that branch (lock guard forces onboarding if
  // it is not yet set up). Req 7.2 — more than one → All stores.
  if (branches.length === 1) {
    const only = branches[0] as StoreResponse;
    return <Navigate to={`/c/${clientId}/store/${only.id}/dashboard`} replace />;
  }
  return <Navigate to={`/c/${clientId}/all/dashboard`} replace />;
}

/**
 * The no-branches empty state (Req 7.3 / 2.6): a client with no branches has no
 * scope to default into, so point the user to Brands to import one. Mirrors the
 * dashboard's own empty copy and the `.card.empty` glass pattern.
 */
function NoBranchesEmptyState({ clientId }: { clientId: string }) {
  return (
    <section>
      <div className="page-head">
        <h2>No branches yet</h2>
      </div>
      <div className="card empty">
        <p>
          This client has no branches yet. Import a brand under{' '}
          <Link to={`/c/${clientId}/brands`}>Brands</Link> to get started.
        </p>
      </div>
    </section>
  );
}

/**
 * The six surface child routes, built from the SURFACES registry so the router
 * and the rail share one source of truth (design §4). An index redirect sends a
 * bare scope path to its dashboard.
 *
 * `branchOnlyGuarded` differs per scope branch (design §5, §8 truth table):
 *
 * - At ALL / BRAND scope (`branchOnlyGuarded: true`) the FLEXIBLE surfaces
 *   (dashboard/orders/statements/payouts) render directly — they aggregate over
 *   the in-scope branches — while the BRANCH-ONLY surfaces (catalog/onboarding)
 *   render through {@link BranchOnlyGuard}, which auto-narrows to the lone
 *   in-scope branch or prompts pick-a-branch (Req 4.2 / 4.5). A direct
 *   `/all/catalog` therefore NEVER renders an aggregate.
 * - At STORE scope (`branchOnlyGuarded: false`) the scope is ALREADY a single
 *   branch, so all six surfaces render directly; the OnboardingLockGuard (task
 *   3.4) handles the not-yet-onboarded case there, not this guard.
 */
function surfaceRoutes({ branchOnlyGuarded }: { branchOnlyGuarded: boolean }) {
  return (
    <>
      <Route index element={<Navigate to="dashboard" replace />} />
      {SURFACES.map((s) => {
        const guardedBranchOnly =
          branchOnlyGuarded && SURFACE_CAPABILITY[s.id] === 'branch-only';
        return (
          <Route
            key={s.id}
            path={s.id}
            element={
              guardedBranchOnly ? (
                <BranchOnlyGuard surface={s.id} />
              ) : (
                <WorkspaceSurface surface={s.id} />
              )
            }
          />
        );
      })}
    </>
  );
}

/**
 * BranchOnlyGuard (task 3.3, Req 4.2 / 4.5 / 5): the element for a BRANCH-ONLY
 * surface (catalog / onboarding) reached at an All or Brand scope. A branch-only
 * surface is meaningless as an aggregate, so this guard — running INSIDE the
 * `<ScopeProvider>` — reads the resolved in-scope branch set and either narrows
 * to a single branch or prompts for one:
 *
 * - EXACTLY ONE in-scope branch → `<Navigate>` to `/c/:clientId/store/:thatId/
 *   <surface>` (Req 4.2 auto-narrow). The store-scope route then renders the
 *   surface for that one branch (behind the onboarding lock, task 3.4).
 * - MORE than one in-scope branch → render {@link PickBranch}, the compact
 *   chooser of the in-scope branches (Req 4.2 / 4.5). The user picks one, which
 *   links to its store scope.
 *
 * Either way a direct `/all/catalog` or `/brand/:id/catalog` NEVER renders an
 * aggregate (Req 4.5) — the `WorkspaceSurface` for a branch-only surface is only
 * ever reached under `store/:storeId`.
 *
 * While the brand/store lists are still loading the context resolves `branches`
 * to the (possibly empty) narrowed set; an empty set simply renders PickBranch
 * with no options rather than mis-narrowing, and the data settles into either
 * the auto-narrow or a populated chooser on the next render.
 */
function BranchOnlyGuard({ surface }: { surface: SurfaceId }) {
  const { clientId } = useParams();
  const { branches, isSingleBranch } = useScopeContext();

  if (isSingleBranch && branches.length === 1 && clientId) {
    const only = branches[0] as StoreResponse;
    return <Navigate to={`/c/${clientId}/store/${only.id}/${surface}`} replace />;
  }

  return <PickBranch surface={surface} />;
}

/**
 * OnboardingLockGuard (task 3.4, Req 5.3 / 5.5 / 8.3): the lock layer inside the
 * `store/:storeId` scope. A store scope is always exactly one branch, so when
 * that branch is NOT-YET-ONBOARDED (`state === 'assigned' && !onboardingComplete`)
 * the only meaningful surface is its onboarding wizard — the data surfaces have
 * no data yet and their reads would be rejected `409 onboarding_required`.
 *
 * The guard:
 *
 * - RE-READS the single store via `useStore(storeId)` (as the old `GatedView` in
 *   `StoreWorkspace` does) so the lock reflects the FRESHEST `onboardingComplete`.
 *   The stores LIST that the scope context resolves from can lag a just-completed
 *   onboarding by a refetch; the single read invalidates on derive, so when
 *   onboarding completes the guard stops redirecting and the full surface set is
 *   revealed with no manual reload (Req 5.5).
 * - Determines the CURRENT surface from the URL's trailing segment (the surface
 *   under `store/:storeId/`), and when the store `isNotYetOnboarded` and that
 *   surface is NOT `onboarding`, `<Navigate>`s to the branch's onboarding surface
 *   (Req 5.3). `onboarding` itself always renders, so the user can complete it.
 * - While the single read is still loading AND the context has no list fallback,
 *   renders a loading state rather than redirecting prematurely (a premature
 *   redirect could bounce a since-onboarded branch back onto onboarding).
 *
 * The `409 onboarding_required` handling on the data read hooks/components
 * (the standalone store pages in `features/store/*`, `queries/index.ts`) is
 * UNCHANGED — this nav-level lock is the primary defense, the hook-level `409`
 * remains as defense-in-depth (Req 8.3).
 */
function OnboardingLockGuard() {
  const { clientId, storeId } = useParams();
  const location = useLocation();
  const { store: scopeStore } = useScopeContext();

  // Freshest read of the single store (Req 5.5). The scope context's `store`
  // (from the list) is the fallback while this settles so a known-onboarded
  // branch never flashes a loading state on navigation.
  const detail = useStore(storeId ?? '', Boolean(storeId));
  // Only trust the single read when it is a real store object (guards against a
  // test/transport returning an unexpected shape); otherwise fall back to the
  // list-resolved store from the scope context.
  const freshBody = detail.data?.status === 200 ? detail.data.body : null;
  const fresh =
    freshBody && typeof freshBody === 'object' && !Array.isArray(freshBody) && 'state' in freshBody
      ? (freshBody as StoreResponse)
      : null;
  const current = fresh ?? scopeStore;

  // The current surface is the trailing path segment under `store/:storeId`.
  // (`/c/:clientId/store/:storeId/<surface>` → the last non-empty segment.)
  const segments = location.pathname.split('/').filter(Boolean);
  const currentSurface = segments[segments.length - 1];

  // No store resolved from either source yet — don't redirect on a guess.
  if (!current) {
    if (detail.isError) {
      return (
        <section>
          <h2>Store</h2>
          <p className="muted">Could not load this branch.</p>
        </section>
      );
    }
    return (
      <section aria-busy="true">
        <h2>Store</h2>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  // Not-yet-onboarded → force onboarding for any other surface (Req 5.3).
  if (isNotYetOnboarded(current) && currentSurface !== 'onboarding' && clientId && storeId) {
    return <Navigate to={`/c/${clientId}/store/${storeId}/onboarding`} replace />;
  }

  // Onboarded (or onboarding itself) → render the surface (Req 5.5).
  return <Outlet />;
}
